import type { MapTileProvider } from "../types/ride";

export interface TileConfig {
  url: string;
  attribution: string;
  /** Highest zoom the tile server actually has detail for; Leaflet upscales beyond this client-side. */
  maxNativeZoom?: number;
}

const ESRI_ATTRIBUTION = "Esri, HERE, Garmin, &copy; OpenStreetMap contributors, and the GIS user community";
const CARTO_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors ' +
  '&copy; <a href="https://carto.com/attributions">CARTO</a>';

/**
 * Basemap tile sources for the ride map. "esri" needs no signup and is verified
 * working (a real tile was downloaded and inspected while wiring this up).
 * "carto" gives a more colorful, Google-Maps-like look but requires a free API
 * key (see README) - its URL is built from CARTO's published docs but hasn't
 * been exercised against a real key here, so double-check it against
 * https://carto.com/basemaps if tiles don't load.
 */
export function getTileConfig(provider: MapTileProvider, apiKey: string | null, theme: "light" | "dark"): TileConfig {
  if (provider === "carto") {
    const style = theme === "dark" ? "dark_all" : "rastertiles/voyager";
    const key = apiKey ? `?key=${encodeURIComponent(apiKey)}` : "";
    return {
      url: `https://basemaps.cartocdn.com/${style}/{z}/{x}/{y}.png${key}`,
      attribution: CARTO_ATTRIBUTION,
    };
  }

  // Esri Canvas basemaps use {z}/{y}/{x} path order, not the usual {z}/{x}/{y}.
  const service = theme === "dark" ? "World_Dark_Gray_Base" : "World_Light_Gray_Base";
  return {
    url: `https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/${service}/MapServer/tile/{z}/{y}/{x}`,
    attribution: ESRI_ATTRIBUTION,
    maxNativeZoom: 16,
  };
}
